import { useCallback, useState } from "react"
import { enqueueSnackbar } from "notistack"
import { MenuItem } from "@mui/material"
import { DateTime } from "luxon"
import { CancelSubscriptionConfirmDialog } from "../../../../../shared/components/subscriptionsTable/CancelSubscriptionConfirmDialog"
import { SubscriptionsTable } from "../../../../../shared/components/subscriptionsTable/SubscriptionsTable"
import { Subscription } from "../../../../merchant/accounting/accounting/accountingApi"
import { cancelSubscription, fetchSubscriptionsByPeriod } from "../subscriptionsApi"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Receipt, ReceiptDialog } from "../../../../receipt/pages/ReceiptDialog"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { TENANT_REPORTS_ROUTE } from "../../../../../constants/routes"
import { useCurrentTenant } from "../../../../tenants/tenantHooks"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { SubscriptionOrder } from "../subscriptionsTypes"

const selectSubscription = (data: SubscriptionOrder[]): Subscription[] => {
    return data.map((order): Subscription => {
        return {
            ...order,
            subscriptionDetails: {
                ...order.subscriptionDetails,
                amount: order.orderLines[0].price,
                customer: {
                    ...order.customer,
                    employeeId: order.customer.employeedId,
                },
                paymentDetails: {
                    ...order.paymentDetails,
                    subscriptionId: "",
                    method: order.paymentDetails.paymentMehod,
                },
                order: {
                    created: order.created,
                    orderType: order.orderType,
                    uid: order.uid,
                    accountingCustomerParty: { name: "-" },
                    id: order.id,
                },
                orderLines: [...order.orderLines],
            },
            name: order.subscriptionDetails.name,
        }
    })
}
type SubscriptionsProps = Readonly<{}>

export function OfficeManagerSubscriptions({}: SubscriptionsProps) {
    const todayDate = DateTime.now()
    const { companyId, userGroupId } = useCurrentTenant()
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: todayDate,
        endDate: todayDate,
    })

    const [receipt, setReceipt] = useState<Receipt>()
    const [subscriptionToCancel, setSubscriptionToCancel] = useState<Subscription>()

    const subscriptionReport = useApiQuery(fetchSubscriptionsByPeriod, {
        queryName: "subscriptions-for-tenant",
        dependencies: {
            companyId,
            userGroupId,
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
        },
        select: selectSubscription,
    })

    const showReceipt = useCallback((row: { id: number; uid: string; orderType: string }) => {
        setReceipt({
            orderId: row.id,
            orderUid: row.uid,
            orderType: row.orderType,
        })
    }, [])

    const { mutateAsync: callCancelSubscription } = useApiMutation(cancelSubscription, {
        async onSuccess(_params, data, context) {
            context.queryClient.invalidateQueries({ queryKey: ["subscriptions-for-tenant"] })
            if (data?.transactionResponse?.subject) {
                enqueueSnackbar({
                    variant: "success",
                    message: data.transactionResponse.subject,
                })
            }
        },
    })

    return (
        <>
            <LocalizedStrict id="tenant-subscriptions-header" attrs={{ title: true }}>
                <PageHeader title="Subscriptions" page={Page.TENANT_SUBSCRIPTIONS}>
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="tenant-nav-item-home">
                            <BreadcrumbLink to={TENANT_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="tenant-nav-item-accounting-header">
                            <Breadcrumb>Accounting</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="tenant-nav-item-subscriptions">
                            <Breadcrumb>Subscriptions</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <SubscriptionsTable
                tableData={subscriptionReport?.data ?? []}
                tableState={{
                    isLoading: subscriptionReport.isLoading,
                    isFetching: subscriptionReport.isFetching,
                    isError: subscriptionReport.isError,
                }}
                rowActionMenuItems={({ row, closeMenu }) => [
                    <MenuItem
                        key={`details-${row.original.id}`}
                        onClick={() => {
                            showReceipt(row.original.subscriptionDetails.order)
                            closeMenu()
                        }}
                    >
                        <LocalizedStrict id="tenant-purchases-tab-subscriptions-details">
                            <span>Details</span>
                        </LocalizedStrict>
                    </MenuItem>,

                    <MenuItem
                        key={`cancel-${row.original.subscriptionDetails.order.id}`}
                        onClick={() => {
                            setSubscriptionToCancel(row.original)
                            closeMenu()
                        }}
                    >
                        <LocalizedStrict id="tenant-purchases-tab-subscriptions-cancel">
                            <span>Cancel subscription</span>
                        </LocalizedStrict>
                    </MenuItem>,
                ]}
                dateState={dateState}
                setDateState={setDateState}
            />
            {receipt && (
                <ReceiptDialog receipt={receipt} open={receipt !== undefined} onClose={() => setReceipt(undefined)} />
            )}
            {subscriptionToCancel && (
                <CancelSubscriptionConfirmDialog
                    subscriptionName={subscriptionToCancel.name}
                    customerName={subscriptionToCancel.subscriptionDetails.customer.displayName}
                    open={Boolean(subscriptionToCancel)}
                    onClose={() => setSubscriptionToCancel(undefined)}
                    onCancelSubscription={(ignoreTerms) => {
                        callCancelSubscription({ orderId: subscriptionToCancel.id, ignoreTerms })
                    }}
                />
            )}
        </>
    )
}
